import api from '@/services/ApiService';
import proxy from '@/services/ProxyService';
import { useInterfaceStore } from '@/store/interface';
import { SALUTATIONS_REVERSE } from './partyManagerUtils';

export default async function saveParty(
  data,
  sources,
  users = [],
  progressReporter,
  oldData,
  tenant,
) {
  const reporter = progressReporter || {
    report: console.log,
    success: console.log,
    error: console.error,
  };
  const INTERFACE_STORE = useInterfaceStore.getState();
  const codemetaId = data.id?.codemeta;
  const weclappId = data.id?.weclapp;
  const tanssId = data.id?.tanss;
  // Transforms the object of possible values into a format suitable for saving
  const obj = Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [
          key,
          value.map((item) => {
            if (typeof item === 'object' && item !== null) {
              item = Object.fromEntries(
                Object.entries(item).map(([k, v]) => {
                  if (v?.selected) {
                    return [k, v[v.selected]];
                  }
                  return [k, v];
                }),
              );
              return item;
            }
          }),
        ];
      }
      if (value.selected) {
        return [key, value[value.selected]];
      }
      return [key, value];
    }),
  );

  const departments =
    obj.departments?.map((d) => d[d.selected]).filter(Boolean) || [];

  const domains = obj.domains?.map((d) => d[d.selected]).filter(Boolean) || [];

  const addressesWithoutId = obj.addresses?.map((a) => {
    const address = { ...a };
    if (address.id !== undefined) {
      delete address.id;
    }
    return address;
  });

  const party = {
    partyType: data.partyType,
    company: obj.company || '',
    company2: obj.company2 || '',
    customer: !!obj.isCustomer,
    supplier: !!obj.isSupplier,
    leadStatus: obj.leadStatus === 'NONE' ? null : obj.leadStatus || null,
    customerNumber: obj.customerNumber?.trim() ? obj.customerNumber : null,
    supplierNumber: obj.supplierNumber?.trim() ? obj.supplierNumber : null,
    birthDate: obj.birthDate || null,
    parentPartyId: obj.parentPartyId || null,
    phone: obj.phone || '',
    fax: obj.fax || '',
    website: obj.website || '',
    mobilePhone1: obj.mobilePhone1 || '',
    mobilePhone2: obj.mobilePhone2 || '',
    firstName: obj.firstName || '',
    lastName: obj.lastName || '',
    phoneHome: obj.phoneHome || '',
    email: obj.email || undefined,
    addresses: addressesWithoutId || [
      {
        countryCode: 'DE',
        primeAddress: true,
      },
    ],
    contacts: obj.contacts || [],
    salutation: obj.salutation || 'NO_SALUTATION',
    referenceNumber: obj.referenceNumber || '',
  };

  const responsibleUserId = obj.responsibleUserId || undefined;
  const assignedOrganizations = obj.assignedOrganizations || [];
  const isPerson = party.partyType === 'PERSON';
  const isOrganization = party.partyType === 'ORGANIZATION';

  /*
   * Create new codemeta party
   */
  let resCodemeta;
  reporter.report('Speichere Daten in Codemeta...');

  if (!codemetaId) {
    const { data } = await api.post('/party', {
      ...party,
      responsibleUserId,
      departments,
      domains,
      sharedWith: [
        {
          type: 'tenant',
          tenant,
          permissions: {
            read: true,
            update: true,
            delete: true,
          },
        },
      ],
    });
    resCodemeta = data;
  } else {
    const { data } = await api.put(`/party/${codemetaId}`, {
      ...party,
      responsibleUserId,
      departments,
      domains,
    });
    resCodemeta = data;
  }

  const cId = resCodemeta?.insertedId ?? codemetaId;
  // Remove all current assigned organizations in codemeta
  if (codemetaId) {
    const currentAssignments =
      (await api.get(`/party?contacts.id=${codemetaId}`))?.data?.result || [];
    await Promise.all(
      currentAssignments.map((org) =>
        api.put(`/party/${org._id}`, {
          contacts: org.contacts?.filter((c) => c.id !== cId) || [],
        }),
      ),
    );
  }

  // Add to all assigned organizations as contact in codemeta
  await Promise.all(
    assignedOrganizations.map(async (org) => {
      const orgData = (await api.get(`/party/${org.id}`))?.data?.result || {};
      if (orgData) {
        await api.put(`/party/${org.id}`, {
          ...orgData,
          contacts: [
            ...(orgData.contacts || []),
            {
              id: cId,
              contact: org.contact || false,
            },
          ],
        });
      }
    }),
  );

  /*
   *  Create weclapp party if no weclappId is provided and weclapp is selected
   */
  let resWeclapp;
  if (sources?.weclapp) {
    reporter.report('Speichere Daten in Weclapp...');
    // [ORGANIZATION] Get weclapp sourceId for parent party if parent is set
    let parentPartyId;
    if (party.parentPartyId) {
      const { data } = await api.get(
        `/datasource?recordId=${party.parentPartyId}&source=weclapp`,
      );
      if (data.result.length > 0) {
        parentPartyId = data.result[0].sourceId;
      } else {
        // TODO: error handling because parent party datasource is missing
      }
    }

    // [ORGANIZATION] Get sourceIds for each contact if the dataSource exists else push to missingContacts
    let contacts = [];
    let missingContacts = [];

    const fetchContacts = async (list = []) =>
      (
        await Promise.all(
          list
            .filter((c) => c.contact)
            .map(async (c) => {
              const { data } = await api.get(
                `/datasource?recordId=${c.id}&source=weclapp`,
              );
              if (data.result.length) return { id: data.result[0].sourceId };
              missingContacts.push(c);
              return null;
            }),
        )
      ).filter(Boolean);

    const { data: wCs } = weclappId
      ? await proxy.get(`${INTERFACE_STORE.weclapp}/party/id/${weclappId}`)
      : { data: {} };

    const weclappContacts = wCs?.contacts || [];
    const currentContacts = await fetchContacts(party.contacts || []);
    const oldContacts = await fetchContacts(oldData?.contacts || []);

    const notSynced = weclappContacts.filter(
      (c) =>
        !currentContacts.some((cc) => cc.id === c.id) &&
        !oldContacts.some((oc) => oc.id === c.id),
    );

    contacts = [...currentContacts, ...notSynced];

    const primaryContact = party.contacts?.find((c) => c.primary === true);
    let primaryContactId = null;
    if (primaryContact) {
      const { data } = await api.get(
        `/datasource?recordId=${primaryContact.id}&source=weclapp`,
      );
      if (data.result.length > 0) {
        primaryContactId = data.result[0].sourceId;
      }
    }

    // Handle create or update of weclapp party
    if (!weclappId) {
      const resultWeclapp = await proxy.post(
        `${INTERFACE_STORE.weclapp}/party`,
        obj.partyType === 'ORGANIZATION' || obj.isCustomer
          ? {
              ...party,
              leadStatus:
                party.leadStatus === 'NONE' ? null : party.leadStatus || null,
              addresses: obj.addresses,
              parentPartyId,
              primaryContactId,
              ...(isOrganization && {
                contacts: contacts.filter(Boolean),
              }),
              ...(responsibleUserId && {
                responsibleUserId:
                  users.find((u) => u._id === responsibleUserId)?.weclappId ||
                  '',
              }),
            }
          : {
              partyType: obj.partyType,
              firstName: obj.firstName || '',
              lastName: obj.lastName || '',
              email: obj.email || '',
              salutation: obj.salutation || 'NO_SALUTATION',
              phone: obj.phone || '',
              mobilePhone1: obj.mobilePhone1 || '',
              mobilePhone2: obj.mobilePhone2 || '',
              phoneHome: obj.phoneHome || '',
              fax: obj.fax || '',
              website: obj.website || '',
            },
      );
      resWeclapp = resultWeclapp.data;
    } else {
      const resultWeclapp = await proxy.put(
        `${INTERFACE_STORE.weclapp}/party/id/${weclappId}?ignoreMissingProperties=true`,
        {
          ...party,
          addresses: obj.addresses,
          leadStatus:
            party.leadStatus === 'NONE' ? null : party.leadStatus || null,
          parentPartyId,
          primaryContactId,
          ...(isOrganization && {
            contacts: contacts.filter(Boolean),
          }),
          ...(responsibleUserId && {
            responsibleUserId:
              users.find((u) => u._id === responsibleUserId)?.weclappId || '',
          }),
          ...(party.birthDate && {
            birthDate: new Date(party.birthDate).getTime() || null,
          }),
        },
      );
      resWeclapp = resultWeclapp.data;
    }

    // Update party at codemeta with customerNumber
    if (
      resWeclapp?.customerNumber !== party.customerNumber &&
      resWeclapp.customerNumber !== ''
    ) {
      await api.put(`/party/${codemetaId || resCodemeta?.insertedId}`, {
        customerNumber: resWeclapp.customerNumber || '',
      });
      party.customerNumber =
        resWeclapp.customerNumber || party?.customerNumber || '';
    }
    // Update party at codemeta with supplierNumber
    if (
      resWeclapp?.supplierNumber !== party.supplierNumber &&
      resWeclapp.supplierNumber !== ''
    ) {
      await api.put(`/party/${codemetaId || resCodemeta?.insertedId}`, {
        supplierNumber: resWeclapp.supplierNumber || '',
      });
      party.supplierNumber =
        resWeclapp.supplierNumber || party?.supplierNumber || '';
    }

    // Create weclapp datasource if needed
    const weclappPartyId = resWeclapp?.id || weclappId;
    if (weclappPartyId) {
      const { data: existingSource } = await api.get(
        `/datasource?sourceId=${weclappPartyId}&recordId=${codemetaId || resCodemeta?.insertedId}&source=weclapp`,
      );
      if (!existingSource?.result?.length) {
        await api.post(`/datasource`, {
          source: 'weclapp',
          sourceId: weclappPartyId.toString(),
          recordId: codemetaId || resCodemeta?.insertedId,
          lastQueryResult: resWeclapp || null,
          sharedWith: [
            {
              type: 'tenant',
              tenant,
              permissions: {
                read: true,
                update: true,
                delete: true,
              },
            },
          ],
        });
      }
    }

    if (isPerson) {
      const oldDataOrgAssignments = (
        await Promise.all(
          (oldData?.assignedOrganizations || []).map(async (org) => {
            if (!org.contact) return null;
            const { data: orgData } = await api.get(
              `/datasource?recordId=${org.id}&source=weclapp`,
            );
            return orgData.result.length > 0
              ? orgData.result[0].sourceId
              : null;
          }),
        )
      ).filter(Boolean);
      const oldDataOrgSourceIds = new Set(oldDataOrgAssignments);

      const newOrgAssignments = (
        await Promise.all(
          assignedOrganizations.map(async (org) => {
            if (!org.contact) return null;
            const { data: orgData } = await api.get(
              `/datasource?recordId=${org.id}&source=weclapp`,
            );
            return orgData.result.length > 0
              ? orgData.result[0].sourceId
              : null;
          }),
        )
      ).filter(Boolean);
      const newOrgSourceIds = new Set(newOrgAssignments);

      const orgsToRemoveFromIds = [...oldDataOrgSourceIds].filter(
        (id) => !newOrgSourceIds.has(id),
      );

      await Promise.all(
        orgsToRemoveFromIds.map(async (orgId) => {
          const { data: orgData } = await proxy.get(
            `${INTERFACE_STORE.weclapp}/party/id/${orgId}`,
          );
          if (orgData.primaryContactId === weclappPartyId) {
            await proxy.put(
              `${INTERFACE_STORE.weclapp}/party/id/${orgId}?ignoreMissingProperties=true`,
              { primaryContactId: null },
            );
          }
          const updatedContacts = (orgData.contacts || []).filter(
            (c) => c.id !== weclappPartyId,
          );
          await proxy.put(
            `${INTERFACE_STORE.weclapp}/party/id/${orgId}?ignoreMissingProperties=true`,
            { contacts: updatedContacts },
          );
        }),
      );

      await Promise.all(
        [...newOrgSourceIds].map(async (orgId) => {
          const { data: orgData } = await proxy.get(
            `${INTERFACE_STORE.weclapp}/party/id/${orgId}`,
          );
          const contacts = orgData.contacts || [];
          const contactExists = contacts.some((c) => c.id === weclappPartyId);
          if (!contactExists) {
            await proxy.put(
              `${INTERFACE_STORE.weclapp}/party/id/${orgId}?ignoreMissingProperties=true`,
              { contacts: [...contacts, { id: weclappPartyId }] },
            );
          }
        }),
      );
    }
  }

  /*
   *  Create tanss party if no tanssId is provided and tanss is selected
   */
  let resTanss;
  if (sources?.tanss) {
    reporter.report('Speichere Daten in Tanss...');
    if (isOrganization || party.customer || party.customerNumber) {
      // [ORGANIZATION] Create Tanss company
      const getTanssTypesObj = (name) => {
        switch (name) {
          case 'Kunde':
            return { id: 1, name: 'Kunde' };
          case 'Lieferant':
            return { id: 2, name: 'Lieferant' };
          case 'Interessent':
            return { id: 3, name: 'Interessent' };
          default:
            break;
        }
      };
      // Get company address
      const primeAddress =
        party?.addresses?.find((address) => address?.primeAddress) ||
        party?.addresses?.[0] ||
        undefined;

      // Get company types
      const types = [];
      if (
        party.customer &&
        !(party.leadStatus && party.leadStatus !== 'CONVERTED')
      )
        types.push(getTanssTypesObj('Kunde'));
      if (party.supplier) types.push(getTanssTypesObj('Lieferant'));
      if (party.leadStatus && party.leadStatus !== 'CONVERTED')
        types.push(getTanssTypesObj('Interessent'));

      Object.keys(INTERFACE_STORE.config.tanssCategories).forEach((key) => {
        const field = INTERFACE_STORE.config.tanssCategories[key].field;
        if (field === 'responsibleUserId') {
          const value =
            INTERFACE_STORE.config.tanssCategories[key]?.map?.[
              responsibleUserId
            ];
          if (value) {
            types.push({
              id: value,
              categoryId: parseInt(key, 10),
            });
          }
        } else if (
          party[field] &&
          INTERFACE_STORE.config.tanssCategories[key].map
        ) {
          const value = party[field];
          if (value) {
            types.push({
              id: value,
              categoryId: parseInt(key, 10),
            });
          }
        }
      });

      // Build company name
      const name = !isOrganization
        ? INTERFACE_STORE.config?.tanssDisplayName === 'firstName lastName'
          ? `${party.firstName || ''}${party.firstName ? ' ' : ''}${party.lastName || ''}`
          : `${party.lastName || ''}${party.lastName && party.firstName ? ', ' : ''}${party.firstName || ''}`
        : `${party.firstName || ''}${party.firstName ? ' ' : ''}${party.lastName || ''}${
            isOrganization ? party.company || '' : ''
          }${isOrganization && party.company2 ? '\n' : ''}${
            isOrganization ? party.company2 || '' : ''
          }`;

      // Transform birthDate to tanss format
      const raw = party?.birthDate;
      let birthday;
      if (raw) {
        const date = new Date(raw);
        date.setDate(date.getDate() + 1);
        birthday = date.toISOString().slice(0, 10);
      }

      let parentPartyId;
      if (party.parentPartyId) {
        const { data } = await api.get(
          `/datasource?recordId=${party.parentPartyId}&source=tanss&sourceType=company`,
        );
        if (data.result.length > 0) {
          parentPartyId = data.result[0].sourceId;
        } else {
          // TODO: error handling because parent party datasource is missing
        }
      }

      const tanssCompany = {
        displayId: party?.customerNumber || party?.supplierNumber,
        name,
        city: primeAddress?.city || '',
        country: primeAddress?.countryCode || '',
        street: primeAddress?.street1 || '',
        postcode: primeAddress?.zipcode || '',
        headquarterId: parentPartyId || 0,
        telephone: party?.phone || '',
        email: party?.email || '',
        telefax: party?.fax || '',
        website: party?.website || '',
        matchcode: party.referenceNumber || '',
        types,
        personalCustomer: !isOrganization ? true : false,
        ...(!isOrganization && {
          personalCustomerEmployee: {
            firstName: party.firstName || '',
            lastName: party.lastName || '',
            salutationId:
              Number(
                Object.keys(SALUTATIONS_REVERSE).find(
                  (k) => SALUTATIONS_REVERSE[Number(k)] === party?.salutation,
                ),
              ) || 0,
            telephoneNumber: party?.phone || '',
            mobilePhone: party?.mobilePhone1 || '',
            mobileNumberTwo: party?.mobilePhone2 || '',
            privatePhoneNumber: party?.phoneHome || '',
            personalFaxNumber: party?.fax || '',
            emailAddress: party?.email || '',
            birthday,
          },
        }),
      };
      if (tanssId) {
        const { data } = await proxy.put(
          `${INTERFACE_STORE.tanss}/companies/${tanssId}`,
          tanssCompany,
        );
        resTanss = data?.content;
      } else {
        const { data } = await proxy.post(
          `${INTERFACE_STORE.tanss}/companies`,
          tanssCompany,
        );
        resTanss = data?.content;
      }

      const getEmployee = async (id) => {
        const {
          data: { content },
        } = await proxy.get(`${INTERFACE_STORE.tanss}/employees/${id}`);
        return content;
      };

      const companyIdStr = String(resTanss.id);

      const oldContactTanssIds = new Set();
      for (const contact of oldData?.contacts || []) {
        const { data: contactData } = await api.get(
          `/datasource?recordId=${contact.id}&source=tanss&sourceType=employee`,
        );
        if (contactData?.result?.length > 0) {
          oldContactTanssIds.add(String(contactData.result[0].sourceId));
        }
      }

      const currentContactTanssIds = new Set();
      for (const contact of party.contacts || []) {
        const { data: contactData } = await api.get(
          `/datasource?recordId=${contact.id}&source=tanss&sourceType=employee`,
        );

        if ((contactData?.result?.length ?? 0) > 0) {
          const tanssContactId = String(contactData.result[0].sourceId);
          currentContactTanssIds.add(tanssContactId);

          const content = await getEmployee(tanssContactId);

          const assignments = Array.isArray(content.companyAssignments)
            ? content.companyAssignments
            : [];

          const idx = assignments.findIndex(
            (a) => String(a.companyId) === companyIdStr,
          );

          const updatedAssignment = {
            ...(idx >= 0 ? assignments[idx] : {}),
            companyId: resTanss.id,
            contactPerson: Boolean(contact.contact ?? false),
          };

          let nextAssignments;
          if (idx >= 0) {
            nextAssignments = assignments.slice();
            nextAssignments[idx] = updatedAssignment;
          } else {
            nextAssignments = [...assignments, updatedAssignment];
          }

          await proxy.put(
            `${INTERFACE_STORE.tanss}/employees/${tanssContactId}`,
            {
              ...content,
              companyAssignments: nextAssignments,
            },
          );
        } else {
        }
      }

      const contactsToRemove = [...oldContactTanssIds].filter(
        (id) => !currentContactTanssIds.has(id),
      );

      for (const employeeId of contactsToRemove) {
        const content = await getEmployee(employeeId);
        const filtered = (content.companyAssignments ?? []).filter(
          (c) => String(c.companyId) !== companyIdStr,
        );

        await proxy.put(`${INTERFACE_STORE.tanss}/employees/${employeeId}`, {
          ...content,
          companyAssignments: filtered,
        });
      }
    } else {
      // [PERSON] Create Tanss person

      // Build person name by config
      const name =
        INTERFACE_STORE.config?.tanssDisplayName === 'firstName lastName'
          ? `${party.firstName || ''}${party.firstName ? ' ' : ''}${party.lastName || ''}`
          : `${party.lastName || ''}${party.lastName && party.firstName ? ', ' : ''}${party.firstName || ''}`;

      // Transform birthDate to tanss format
      const raw = party?.birthDate;
      let birthday;
      if (raw) {
        const date = new Date(raw);
        date.setDate(date.getDate() + 1);
        birthday = date.toISOString().slice(0, 10);
      }

      // Get company assignments
      let companyAssignments = [];
      let missingCompanies = [];
      if (assignedOrganizations && assignedOrganizations.length > 0) {
        companyAssignments = await Promise.all(
          assignedOrganizations.map(async (cA) => {
            const { data: contactData } = await api.get(
              `/datasource?recordId=${cA.id}&source=tanss&sourceType=company`,
            );
            if (contactData.result.length > 0) {
              return {
                companyId: contactData.result[0].sourceId,
                contactPerson: cA.contact || false,
              };
            } else {
              missingCompanies.push(cA);
              return null;
            }
          }),
        );
      }

      const tanssPerson = {
        name,
        firstName: party.firstName || '',
        lastName: party.lastName || '',
        initials: `${party.firstName?.substring(0, 1).toUpperCase() || ''}${party.lastName?.substring(0, 1).toUpperCase() || ''}`,
        salutationId:
          Number(
            Object.keys(SALUTATIONS_REVERSE).find(
              (k) => SALUTATIONS_REVERSE[Number(k)] === party?.salutation,
            ),
          ) || 0,
        telephoneNumber: party?.phone || '',
        // telephoneNumberTwo: party?.phone2 || '',
        mobilePhone: party?.mobilePhone1 || '',
        mobileNumberTwo: party?.mobilePhone2 || '',
        privatePhoneNumber: party?.phoneHome || '',
        personalFaxNumber: party?.fax || '',
        emailAddress: party?.email || '',
        companyAssignments,
        birthday,
        active: true,
      };

      if (tanssId) {
        const { data } = await proxy.put(
          `${INTERFACE_STORE.tanss}/employees/${tanssId}`,
          tanssPerson,
        );
        resTanss = data?.content;
      } else {
        const { data } = await proxy.post(
          `${INTERFACE_STORE.tanss}/employees`,
          tanssPerson,
        );
        resTanss = data?.content;
      }
    }

    // Create tanss datasource if needed
    const tanssPartyId = resTanss?.id || tanssId;
    if (tanssPartyId) {
      const sourceType = isOrganization ? 'company' : 'employee';
      const { data: existingSource } = await api.get(
        `/datasource?sourceId=${tanssPartyId}&recordId=${codemetaId}&source=tanss&sourceType=${sourceType}`,
      );
      if (!existingSource?.result?.length) {
        await api.post(`/datasource`, {
          source: 'tanss',
          sourceType,
          sourceId: tanssPartyId.toString(),
          recordId: codemetaId || resCodemeta?.insertedId,
          lastQueryResult: resTanss || null,
          sharedWith: [
            {
              type: 'tenant',
              tenant,
              permissions: {
                read: true,
                update: true,
                delete: true,
              },
            },
          ],
        });
      }
    }
  }
}
